/**
 * CI-2 — read and resolve CSE (Claims Structured Emission) internal conflicts.
 *
 * CSE extracts structured `report.claims[]` at gen-time. When two claims
 * share the same (reportSectionId, field) but disagree on `value`, the
 * pipeline stamps `qualityWarnings` with type `cse-internal-conflict`.
 */
import type { ReportClaim } from '../types/citation';
import type { SiteRagReport } from '../types/siteRag';
import { areAllEquivalentUnknownClaimValues, detectClaimConflicts, type ClaimConflict } from '../services/claimExtraction';

export const CSE_CONFLICT_EXPLAINER =
  'CSE (Claims Structured Emission) is the post-generation pass that turns report prose into structured claims (`report.claims[]`). '
  + 'A conflict means the AI extracted different values for the same field in this report. '
  + 'Record the value you trust as a separate review; the original report and its warning stay unchanged. Correct the source evidence and regenerate to produce a revised report.';

export const CSE_CONFLICT_SOURCE = {
  file: 'src/services/claimExtraction.ts',
  line: 611,
  label: 'detectClaimConflicts',
} as const;

export function cseConflictKey(reportSectionId: string, field: string): string {
  return `${reportSectionId.trim()}:::${field.trim()}`;
}

/** Flatten all `cse-internal-conflict` entries from a report. */
export function extractCseConflictEntries(report: SiteRagReport): ClaimConflict[] {
  const warnings = Array.isArray(report.qualityWarnings) ? report.qualityWarnings : [];
  const out: ClaimConflict[] = [];
  for (const w of warnings) {
    if (w.type !== 'cse-internal-conflict') continue;
    const entries = (w as {
      entries?: ReadonlyArray<{ reportSectionId: string; field: string; values: ReadonlyArray<string> }>;
    }).entries;
    if (Array.isArray(entries)) {
      for (const entry of entries) {
        if (areAllEquivalentUnknownClaimValues(entry.values)) continue;
        out.push({
          reportSectionId: entry.reportSectionId,
          field: entry.field,
          values: [...entry.values],
        });
      }
    }
  }
  return out;
}

export function countCseConflictEntries(report: SiteRagReport): number {
  return extractCseConflictEntries(report).length;
}

/** Remove CSE conflict warnings; leave other quality warnings intact. */
export function stripCseConflictWarnings(
  warnings: SiteRagReport['qualityWarnings'] | undefined,
): SiteRagReport['qualityWarnings'] {
  if (!Array.isArray(warnings)) return warnings;
  return warnings.filter(w => w.type !== 'cse-internal-conflict');
}

/**
 * Keep claims that match the user's chosen value for each conflicting
 * (section, field) pair; drop competing claims for resolved keys.
 */
export function resolveCseConflictsInClaims(
  claims: ReadonlyArray<ReportClaim>,
  resolutions: ReadonlyMap<string, string>,
): ReportClaim[] {
  if (resolutions.size === 0) return [...claims];
  return claims.filter((claim) => {
    const sec = typeof claim.reportSectionId === 'string' ? claim.reportSectionId.trim() : '';
    const field = typeof claim.field === 'string' ? claim.field.trim() : '';
    if (!sec || !field) return true;
    const key = cseConflictKey(sec, field);
    if (!resolutions.has(key)) return true;
    const chosen = resolutions.get(key)!;
    const normalised = claim.value == null ? '' : String(claim.value).trim();
    return normalised === chosen;
  });
}

/** Apply resolutions and re-detect; returns patched report slices. */
export function applyCseConflictResolutions(
  report: SiteRagReport,
  resolutions: ReadonlyMap<string, string>,
): Pick<SiteRagReport, 'claims' | 'qualityWarnings'> {
  const nextClaims = resolveCseConflictsInClaims(report.claims ?? [], resolutions);
  const remaining = detectClaimConflicts(nextClaims);
  const baseWarnings = stripCseConflictWarnings(report.qualityWarnings);
  if (remaining.length === 0) {
    return { claims: nextClaims, qualityWarnings: baseWarnings };
  }
  return {
    claims: nextClaims,
    qualityWarnings: [
      ...(baseWarnings ?? []),
      {
        type: 'cse-internal-conflict' as const,
        count: remaining.length,
        entries: remaining,
      },
    ],
  };
}

export function cseConflictSourceUrl(): string {
  return `https://github.com/williamtheron-deep/Deep-Atomic-Platform/blob/main/${CSE_CONFLICT_SOURCE.file}#L${CSE_CONFLICT_SOURCE.line}`;
}

const MAX_TOOLTIP_ENTRIES = 3;
const MAX_TOOLTIP_VALUES = 3;

export function hasCseConflicts(report: SiteRagReport): boolean {
  return extractCseConflictEntries(report).length > 0;
}

/** Compact tooltip for the chip — first few collisions only. */
export function buildCseConflictTooltip(report: SiteRagReport): string {
  const entries = extractCseConflictEntries(report);
  if (entries.length === 0) return '';

  const lines: string[] = [];
  for (let i = 0; i < Math.min(entries.length, MAX_TOOLTIP_ENTRIES); i += 1) {
    const entry = entries[i]!;
    const trimmedValues = entry.values.slice(0, MAX_TOOLTIP_VALUES);
    const more = entry.values.length > MAX_TOOLTIP_VALUES
      ? ` (+${entry.values.length - MAX_TOOLTIP_VALUES} more)`
      : '';
    lines.push(`${entry.reportSectionId}.${entry.field}: ${trimmedValues.join(' ≠ ')}${more}`);
  }
  if (entries.length > MAX_TOOLTIP_ENTRIES) {
    lines.push(`…and ${entries.length - MAX_TOOLTIP_ENTRIES} more`);
  }
  lines.push('Click to review and pick the canonical value.');
  return lines.join('\n');
}
